import React, { useEffect, useState } from "react";
import "./ResidentRentals.css";

const BEDROOM_OPTIONS = ["Studio", "1 BHK", "2 BHK", "3 BHK", "4+ BHK"];

export default function ResidentRentals() {
  const residentTower = localStorage.getItem("residentTower") || "your tower";

  const [form, setForm] = useState({
    title: "",
    description: "",
    rentAmount: "",
    bedrooms: "",
    availableFrom: "",
    contactPhone: "",
  });
  const [listings, setListings] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const API = process.env.REACT_APP_API_URL;
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("residentToken")}` };

  const loadMine = async () => {
    try {
      const res = await fetch(`${API}/api/rentals/mine`, { headers: authHeader });
      if (!res.ok) {
        setError("Failed to load your listings");
        return;
      }
      setListings(await res.json());
    } catch (err) {
      setError("Server error while loading listings");
    }
  };

  const prefillContactPhone = async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: authHeader });
      if (!res.ok) return;
      const profile = await res.json();
      if (profile.phone && profile.phone !== "Not provided") {
        setForm((prev) => (prev.contactPhone ? prev : { ...prev, contactPhone: profile.phone }));
      }
    } catch (err) {
      // silently ignore — contact phone just stays empty for manual entry
    }
  };

  useEffect(() => {
    loadMine();
    prefillContactPhone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.title || !form.description || !form.rentAmount) {
      setError("Title, description, and rent amount are required");
      return;
    }

    try {
      const res = await fetch(`${API}/api/rentals`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to publish listing");
      }

      setForm({ title: "", description: "", rentAmount: "", bedrooms: "", availableFrom: "", contactPhone: "" });
      setSuccess("Listing published");
      loadMine();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleStatus = async (listing) => {
    const newStatus = listing.status === "Available" ? "Rented" : "Available";
    await fetch(`${API}/api/rentals/${listing.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeader },
      body: JSON.stringify({ status: newStatus }),
    });
    loadMine();
  };

  const deleteListing = async (id) => {
    await fetch(`${API}/api/rentals/${id}`, { method: "DELETE", headers: authHeader });
    loadMine();
  };

  return (
    <div className="resident-layout">
      
      <div className="resident-content">
        <h1 className="rentals-title">Post a Rental Listing</h1>

        {error && <p className="rentals-error">{error}</p>}
        {success && <p className="rentals-success">{success}</p>}

        <form onSubmit={handleSubmit} className="rentals-form">
          <label className="rentals-label">
            Title
            <input
              name="title"
              className="rentals-input"
              placeholder={`e.g. Spacious 2 BHK for rent in ${residentTower}`}
              value={form.title}
              onChange={handleChange}
            />
          </label>

          <label className="rentals-label">
            Description
            <textarea
              name="description"
              className="rentals-textarea"
              rows={5}
              placeholder="Describe the apartment — layout, furnishing, view, amenities nearby, etc."
              value={form.description}
              onChange={handleChange}
            />
          </label>

          <div className="rentals-form-row">
            <label className="rentals-label">
              Monthly Rent (₹)
              <input
                type="number"
                name="rentAmount"
                className="rentals-input"
                value={form.rentAmount}
                onChange={handleChange}
              />
            </label>

            <label className="rentals-label">
              Bedrooms
              <select name="bedrooms" className="rentals-input" value={form.bedrooms} onChange={handleChange}>
                <option value="">Select</option>
                {BEDROOM_OPTIONS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="rentals-form-row">
            <label className="rentals-label">
              Available From
              <input
                type="date"
                name="availableFrom"
                className="rentals-input"
                value={form.availableFrom}
                onChange={handleChange}
              />
            </label>

            <label className="rentals-label">
              Contact Phone
              <input
                name="contactPhone"
                className="rentals-input"
                value={form.contactPhone}
                onChange={handleChange}
              />
              <span className="rentals-hint">Prefilled from your profile — edit if you'd like a different number for inquiries</span>
            </label>
          </div>

          <button type="submit" className="rentals-submit">Publish Listing</button>
        </form>

        <h2 className="rentals-list-heading">My Listings</h2>

        <div className="rentals-list">
          {listings.map((l) => (
            <div className="rentals-item" key={l.id}>
              <div className="rentals-item-header">
                <span className="rentals-item-title">{l.title}</span>
                <span className={`rentals-status-badge status-${l.status}`}>{l.status}</span>
              </div>
              <div className="rentals-item-desc">{l.description}</div>
              <div className="rentals-item-meta">
                ₹{l.rentAmount}/month · {l.bedrooms} · Available {l.availableFrom}
              </div>
              <div className="rentals-item-actions">
                <button className="rentals-toggle-btn" onClick={() => toggleStatus(l)}>
                  Mark as {l.status === "Available" ? "Rented" : "Available"}
                </button>
                <button className="rentals-delete-btn" onClick={() => deleteListing(l.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}

          {listings.length === 0 && !error && (
            <p className="rentals-empty">You haven't posted any listings yet</p>
          )}
        </div>
      </div>
    </div>
  );
}